import { isApplicationCode, type ApplicationCode } from '@shared/config/applications';
import { PERMISSIONS } from '@shared/constants/permissions';
import type {
  AuthorizationData,
  AuthSession,
  AuthUser,
  PermissionScope,
  PermissionScopeEntry,
} from '@shared/types/auth';

/**
 * Pure authorization helpers.
 *
 * The backend is the source of truth: it returns the caller's effective
 * `isOwner` flag, permission keys and per-permission scopes. Everything here is
 * a read-only projection of that payload used to decide what the UI renders.
 * Nothing in this file grants access — the API re-checks every request.
 */

/** Wildcard key a backend may emit for "everything" (`*`) or a module (`employees.*`). */
const WILDCARD = '*';

export const EMPTY_AUTHORIZATION: AuthorizationData = {
  isOwner: false,
  applications: [],
  permissions: [],
  scopes: {},
  employeeGrantablePermissions: [],
};

function toPermissionList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
}

function toApplications(value: unknown): ApplicationCode[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isApplicationCode);
}

function toScopeMap(value: unknown): Record<string, PermissionScopeEntry> {
  if (!value || typeof value !== 'object') return {};
  const entries = Object.entries(value as Record<string, unknown>).flatMap(([key, raw]) => {
    // Tolerate both `{ scope: 'team' }` and a bare `'team'` string.
    const scope = typeof raw === 'string' ? raw : (raw as PermissionScopeEntry | null)?.scope;
    return typeof scope === 'string' ? [[key, { scope: scope as PermissionScope }] as const] : [];
  });
  return Object.fromEntries(entries);
}

/**
 * Normalize the authorization payload out of an `/auth/me` user or a
 * login/refresh session. Both shapes are accepted so a backend that nests the
 * data under `user` and one that returns it on the session root both work.
 */
export function resolveAuthorization(
  source: AuthUser | AuthSession | null | undefined,
  fallback?: AuthUser | AuthSession | null,
): AuthorizationData {
  const primary = (source ?? {}) as Partial<AuthorizationData>;
  const secondary = (fallback ?? {}) as Partial<AuthorizationData>;
  const permissions = toPermissionList(primary.permissions ?? secondary.permissions);
  const scopes = toScopeMap(primary.scopes ?? secondary.scopes);
  const isOwner = Boolean(primary.isOwner ?? secondary.isOwner);
  const employeeGrantablePermissions = toPermissionList(
    primary.employeeGrantablePermissions ?? secondary.employeeGrantablePermissions,
  );
  const applications = toApplications(primary.applications ?? secondary.applications);
  return { isOwner, applications, permissions, scopes, employeeGrantablePermissions };
}

/**
 * Did this payload actually carry authorization data?
 *
 * Not every endpoint returns it: `/auth/me` carries `isOwner` / `permissions` /
 * `scopes`, while a login or cookie-refresh response may return only the user
 * identity. Treating a payload without the block as "no permissions" silently
 * strips an owner of every affordance, so callers must distinguish "absent"
 * from "empty" and keep what they already had.
 */
export function hasAuthorizationPayload(
  source: AuthUser | AuthSession | null | undefined,
): boolean {
  const record = (source ?? {}) as Partial<AuthorizationData>;
  return typeof record.isOwner === 'boolean' || Array.isArray(record.permissions);
}

/** Index the permission list once so lookups stay O(1) per check. */
export function toPermissionSet(permissions: readonly string[]): ReadonlySet<string> {
  return new Set(permissions);
}

/**
 * Single permission check. Owners bypass (mirroring the backend) — except for
 * employee-grantable keys (Finance & Accounts), which the backend requires to
 * be actually held — through a role or an employee grant — owner or not (the Owner's authority to administer them is
 * the single exception). Both the global `*` and a `module.*`
 * wildcard are honored if the backend emits them.
 */
export function checkPermission(
  authorization: AuthorizationData,
  permissionSet: ReadonlySet<string>,
  permission: string,
): boolean {
  if (authorization.employeeGrantablePermissions?.includes(permission)) {
    // The one exception, mirroring the backend's `canAdministerFinanceGrants`:
    // the Owner holds no Finance key automatically, but has the AUTHORITY to
    // administer employee Finance grants.
    if (permission === PERMISSIONS.finance.manageAccess && authorization.isOwner) return true;
    return permissionSet.has(permission);
  }
  if (authorization.isOwner) return true;
  if (!permission) return false;
  if (permissionSet.has(permission) || permissionSet.has(WILDCARD)) return true;
  const separator = permission.indexOf('.');
  if (separator > 0) {
    return permissionSet.has(`${permission.slice(0, separator)}.${WILDCARD}`);
  }
  return false;
}

/** Backend scope granted for a permission, or `null` when it isn't granted. */
export function resolveScope(
  authorization: AuthorizationData,
  permission: string,
): PermissionScope | null {
  if (authorization.scopes[permission]) return authorization.scopes[permission].scope;
  return authorization.isOwner ? 'all' : null;
}

/**
 * May this account open the application? Pure data check — there is no Owner
 * bypass for application access (the backend's ApplicationAccessGuard has
 * none either; the Owner simply holds every application).
 */
export function hasApplication(
  authorization: Pick<AuthorizationData, 'applications'>,
  application: ApplicationCode,
): boolean {
  return authorization.applications.includes(application);
}
